import { useEffect, useState } from "react";
import { EmojiPickerProps } from "../utils/types/types";
import {
  EmojiItem,
  getPopularEmojiItems,
  searchEmojiItems,
} from "../../../utils/functions/emoji-extractor";
import { emojiList } from "../../../utils/constants/emojiList";
import { visibleScrollbar } from "../../../utils/functions/get-visible-scrollbar";

const EmojiPicker = ({ editor, onClose }: EmojiPickerProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [filteredEmojis, setFilteredEmojis] = useState<EmojiItem[]>([]);

  useEffect(() => {
    let emojis: EmojiItem[] = [];

    if (searchTerm.trim()) {
      emojis = searchEmojiItems(emojiList, searchTerm);
    } else if (selectedCategory) {
      emojis = emojiList.filter((emoji) => emoji.category === selectedCategory);
    } else {
      emojis = getPopularEmojiItems(emojiList, 100);
    }

    setFilteredEmojis(emojis.slice(0, 100));
  }, [selectedCategory, searchTerm]);

  const handleEmojiClick = (emoji: EmojiItem) => {
    editor.chain().focus().insertContent(emoji.emoji).run();
    onClose();
  };

  const getCategoryTabs = () => {
    const categories = [...new Set(emojiList.map((emoji) => emoji.category))];
    const filteredCategories = categories.filter(
      (category) => category !== "Component"
    );
    return filteredCategories.map((category) => ({
      name: category,
      label: category,
    }));
  };

  return (
    <div className="px-[12px] py-[10px] w-[300px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px]">
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search emojis"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-border-gray-375 dark:border-gray-375-dk rounded-[8px] text-xs focus:outline-none text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {!searchTerm && (
        <div className="mb-3 flex flex-wrap gap-1">
          <button
            onClick={() => setSelectedCategory("")}
            className={`px-2 py-1 text-xxs rounded text-fixed-white dark:text-fixed-white-dk ${
              selectedCategory === ""
                ? "bg-verba-blue dark:bg-verba-blue-dk"
                : "bg-gray-75 dark:bg-gray-375 hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            }`}
          >
            Popular
          </button>
          {getCategoryTabs().map((tab) => (
            <button
              key={tab.name}
              onClick={() => setSelectedCategory(tab.name)}
              className={`px-2 py-1 text-xxs rounded text-fixed-white dark:text-fixed-white-dk ${
                selectedCategory === tab.name
                  ? "bg-verba-blue dark:bg-verba-blue-dk"
                  : "bg-gray-75 dark:bg-gray-375 hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div
        className={`grid grid-cols-10 gap-x-2 gap-y-4 max-h-[200px] overflow-y-auto ${visibleScrollbar(
          "[&::-webkit-scrollbar]:h-[8px] [&::-webkit-scrollbar]:w-[8px]"
        )}`}
      >
        {filteredEmojis.map((emoji, index) => (
          <button
            key={`${emoji.code.join("-")}-${index}`}
            onClick={() => handleEmojiClick(emoji)}
            className="w-[18px] h-[18px] flex items-center justify-center text-[18px] hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded cursor-pointer transition-colors"
            title={emoji.name}
          >
            {emoji.emoji}
          </button>
        ))}
      </div>

      {filteredEmojis.length === 0 && (
        <div className="text-center text-md text-heading-text dark:text-heading-text-dk py-4">
          No emojis found
        </div>
      )}
    </div>
  );
};

export default EmojiPicker;
